/**
 * Approval Center — the Manager / Lead / Executive stage.
 *
 * The queue is derived server-side from the pending approval step, not from the
 * claim's status, so a claim only ever appears here for the one person the org
 * chart routed it to. There is nothing to "take": ownership is the routing.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  fmtDate,
  money,
  statusLabel,
  type ApprovalRole,
  type ClaimStatus,
} from '@gdec/shared';
import { api } from '@/api/client';
import { useCurrentUser } from '@/auth/AuthContext';
import { StatusBadge } from '@/components/Badge';
import { ClaimViewer } from '@/components/ClaimViewer';
import { DataTable, type Column } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import {
  ActionDialog,
  ErrorNote,
  QueuePanel,
  toTs,
  useAction,
  useQueue,
} from '@/components/QueueKit';
import { LoadingBlock } from '@/components/Spinner';
import { useToast } from '@/components/Toast';

interface ApprovalQueueRow {
  id: string;
  period: string;
  status: ClaimStatus;
  claimedAmount: number;
  createdAt: string;
  requesterName: string;
  requesterDepartment: string;
  myRole: ApprovalRole;
  rowCount: number;
}

type Pending =
  | { kind: 'approve'; row: ApprovalQueueRow }
  | { kind: 'disapprove'; row: ApprovalQueueRow };

function roleVerb(role: ApprovalRole): string {
  return role === 'Checked By' ? 'check' : 'approval';
}

export function ApprovalsPage(): ReactNode {
  const user = useCurrentUser();
  const { toast } = useToast();

  const load = useCallback(() => api.approvals.queue<ApprovalQueueRow[]>(), []);
  const { rows, error, reload } = useQueue<ApprovalQueueRow>(load);

  const [viewing, setViewing] = useState<ApprovalQueueRow | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [reason, setReason] = useState('');
  const action = useAction();

  const decide = useCallback(
    async (row: ApprovalQueueRow, approve: boolean, why: string): Promise<void> => {
      const ok = await action.run(async () => {
        await api.approvals.decide<unknown>({ claimId: row.id, approve, reason: why });
      });
      if (!ok) return;
      toast(
        approve
          ? `${row.id} recorded as ${row.myRole === 'Checked By' ? 'checked' : 'approved'}.`
          : `${row.id} disapproved and returned to ${row.requesterName}.`,
        'ok',
      );
      setPending(null);
      setReason('');
      setViewing(null);
      await reload();
    },
    [action, toast, reload],
  );

  const columns: ReadonlyArray<Column<ApprovalQueueRow>> = useMemo(
    () => [
      { key: 'id', header: 'Request ID', cell: (r) => <b>{r.id}</b> },
      { key: 'requester', header: 'Requestor', cell: (r) => r.requesterName },
      { key: 'department', header: 'Department', cell: (r) => r.requesterDepartment },
      { key: 'period', header: 'Period', cell: (r) => r.period },
      { key: 'lines', header: 'Receipts', align: 'right', cell: (r) => r.rowCount },
      {
        key: 'claimed',
        header: 'Claimed',
        align: 'right',
        cell: (r) => money(r.claimedAmount),
      },
      {
        key: 'step',
        header: 'Your Step',
        cell: (r) => <span className="tag-chip">{r.myRole}</span>,
      },
      { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
      { key: 'submitted', header: 'Submitted', cell: (r) => fmtDate(toTs(r.createdAt)) },
      {
        key: 'actions',
        header: '',
        cell: (r) => (
          <div className="row-actions">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setViewing(r)}
            >
              View
            </button>
            <button
              type="button"
              className="btn btn-good btn-sm"
              onClick={() => setPending({ kind: 'approve', row: r })}
            >
              {r.myRole === 'Checked By' ? '✓ Mark Checked' : '✓ Approve'}
            </button>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => {
                setReason('');
                setPending({ kind: 'disapprove', row: r });
              }}
            >
              ✕ Disapprove
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  const names = useMemo(() => ({ [user.id]: user.name }), [user.id, user.name]);
  const trimmedReason = reason.trim();

  return (
    <>
      <ErrorNote message={error} />

      <QueuePanel title="Pending My Action" count={rows?.length}>
        <p className="hint">
          You are confirming that this expense is legitimate and complete — not the amount that
          will be paid. Finance sets that separately in the next stage.
        </p>
        {rows === null && !error ? (
          <LoadingBlock label="Loading your approval queue…" />
        ) : (
          <DataTable
            rows={rows ?? []}
            columns={columns}
            rowKey={(r) => r.id}
            empty={
              <EmptyState
                icon="🎉"
                title="No Pending Approvals"
                description="You're all caught up."
              />
            }
          />
        )}
      </QueuePanel>

      {viewing ? (
        <ClaimViewer
          claimId={viewing.id}
          requesterName={viewing.requesterName}
          names={names}
          onClose={() => setViewing(null)}
        />
      ) : null}

      {pending?.kind === 'approve' ? (
        <ActionDialog
          title={`${pending.row.myRole === 'Checked By' ? 'Mark checked' : 'Approve'} ${pending.row.id}`}
          confirmLabel={pending.row.myRole === 'Checked By' ? 'Mark as Checked' : 'Approve Claim'}
          busy={action.busy}
          error={action.error}
          onCancel={() => setPending(null)}
          onConfirm={() => void decide(pending.row, true, '')}
        >
          <p className="hint">
            Recording your {roleVerb(pending.row.myRole)} on{' '}
            <b>{money(pending.row.claimedAmount)}</b> claimed by{' '}
            <b>{pending.row.requesterName}</b> ({pending.row.requesterDepartment}).
          </p>
          <p className="hint tight">
            The claim moves to the next step in its approval chain, or to Finance Review if you
            are the last approver. Current status: {statusLabel(pending.row.status)}.
          </p>
        </ActionDialog>
      ) : null}

      {pending?.kind === 'disapprove' ? (
        <ActionDialog
          title={`Disapprove ${pending.row.id}`}
          confirmLabel="Disapprove Claim"
          danger
          busy={action.busy}
          error={action.error}
          disabled={trimmedReason.length === 0}
          onCancel={() => setPending(null)}
          onConfirm={() => void decide(pending.row, false, trimmedReason)}
        >
          <p className="hint bad">
            This sends {pending.row.id} back to {pending.row.requesterName} as
            &ldquo;Disapproved / For Resubmission&rdquo;. They will see your reason and can edit
            and resubmit the claim.
          </p>
          <div className="field">
            <label htmlFor="disapprove-reason">Reason (required)</label>
            <textarea
              id="disapprove-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Missing supporting document for the 12 Feb receipt."
            />
          </div>
          {trimmedReason.length === 0 ? (
            <p className="hint tight">A reason is required before you can disapprove.</p>
          ) : null}
        </ActionDialog>
      ) : null}
    </>
  );
}

/* `setError` is exposed by useQueue for callers that need to clear a stale
   banner; this page clears it implicitly on every reload. */
void 0;
